import React, { useCallback, useEffect, useState } from "react";
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableFooter,
  Paper,
  Button,
  Select,
  MenuItem,
  Input,
  Grid,
  Box,
  Typography,
  Chip,
  Pagination,
  IconButton,
} from "@mui/material";
import * as XLSX from "xlsx";
import { parseISO } from "date-fns";
import { debounce } from "lodash-es";
import { DataTableList } from "./interfaces/TableData";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { translate } from "./util/translationUtil";

export default function DataTable<T extends object>({
  data,
  columns,
  defaultPageSize = 10,
  defaultOrderByColumn = "",
  onFilter,
  onClick,
  actionButtons,
  exportAgentMethod,
  clickUrl,
  totalColumn,
  disableRowClick = false,
}: Props<T>) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(data.currentPage);
  const [pageSize, setPageSize] = useState(data.pageSize);
  const [orderBy, setOrderBy] = useState(defaultOrderByColumn);
  const [sortDirection, setSortDirection] = useState("asc");
  const [columnFilters, setColumnFilters] = useState<{ [key: string]: string }>(
    {}
  );

  const flattenData = <T extends object>(data: T[]): any[] => {
    return data.map((item) => {
      const flattenedItem: any = {};
      for (const key in item) {
        if (typeof item[key] === "object" && item[key] !== null) {
          for (const nestedKey in item[key]) {
            flattenedItem[`${key}.${nestedKey}`] = item[key][nestedKey];
          }
        } else {
          flattenedItem[key] = item[key];
        }
      }
      return flattenedItem;
    });
  };
  const flattenedData = flattenData(Array.from(data.list.values()));

  const totalAmount = totalColumn
    ? flattenedData.reduce(
      (total, item) => total + parseFloat(item[totalColumn] || 0),
      0
    )
    : null;

  const formatValue = (value: any, column?: DataTableColumn<T>) => {

    if (column?.dataType === DataType.DATE) {
      if (typeof value === "string") {
        const date = parseISO(value);
        const formattedDate = date?.toLocaleDateString();
        return date instanceof Date && !isNaN(date.getTime())
          ? formattedDate
          : "";
      }

      if (value instanceof Date) {
        return !isNaN(value.getTime()) ? value.toLocaleDateString() : "";
      }
      return "";
    }

    if (column?.dataType === DataType.BOOLEAN) {
      return value ? <i className="check square icon"></i> : "";
    }

    if (column?.dataType === DataType.NUMBER && column?.formatPrice) {
      return value?.toLocaleString("de-DE", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }

    if (column?.dataType === DataType.SELECT) {
      const item = column?.options?.filter((x) => x.value === value)?.[0];
      return item?.text || value;
    }

    return value;
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const debouncedFilter = useCallback(
    debounce((filters: any) => {
      if (onFilter) {
        onFilter({
          filters,
          page: currentPage,
          pageSize,
          orderBy,
          sortDirection,
        });
      }
    }, 300),
    []
  );

  const handleFilterChange = (
    columnKey: string,
    value: any,
    dataType: DataType = DataType.TEXT
  ) => {
    let updatedFilters = {};

    if (value === null || value === "") {
      const { [columnKey]: _, ...updatedFilters } = columnFilters;
      setColumnFilters(updatedFilters);
    } else {
      const data = dataType === DataType.NUMBER ? +value : value;
      updatedFilters = { ...columnFilters, [columnKey]: data };
      setColumnFilters(updatedFilters);
    }

    debouncedFilter(updatedFilters);
  };

  const handlePageSizeChange = (
    _event: React.SyntheticEvent<HTMLElement>,
    data: any
  ) => {
    const newSize =
      parseInt(data.value as string, 10) || defaultPageSize || 100;
    setPageSize(newSize);
    setCurrentPage(1);
  };

  const handleOrderByChange = (
    _event: React.SyntheticEvent<HTMLElement>,
    columnKey: string
  ) => {
    const newSortDirection =
      orderBy === columnKey && sortDirection === "asc" ? "desc" : "asc";

    setOrderBy(columnKey);
    setSortDirection(newSortDirection);
  };

  const exportToExcel = async () => {
    if (exportAgentMethod == null) {
      return;
    }

    try {
      const response = await exportAgentMethod({
        filters: columnFilters,
        orderBy,
        sortDirection,
      });
      const worksheet = XLSX.utils.json_to_sheet(response);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

      const excelBuffer = XLSX.write(workbook, {
        type: "array",
        bookType: "xlsx",
      });

      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "data.xlsx";

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
    } catch (error) {
      console.error("Error exporting data:", error);
    }
  };

  const pageSizeOptions = [10, 20, 50, 100];
  const totalPages = Math.ceil(data.totalRecords / pageSize);

  useEffect(() => {
    onFilter &&
      onFilter({
        filters: columnFilters,
        page: currentPage,
        sortDirection,
        pageSize,
        orderBy,
      });
  }, [currentPage, pageSize, orderBy, sortDirection]);

  const handleRowClick = (item: any) => {
    if (disableRowClick) return;
    const selection = window.getSelection();
    if (selection && selection.toString() !== "") return;

    if (onClick) onClick(item);
    else if (clickUrl) navigate(clickUrl(item));
  };

  return (
    <Box sx={{ maxWidth: "100%", mt: 1, userSelect: "text" }}>
      <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
        <Table size="small" sx={{ minWidth: { xs: 650, sm: "auto" } }}>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.key}
                  sx={{
                    verticalAlign: "top",
                    width: column.width || "auto",
                    py: { xs: 0.75, sm: 1 },
                    px: { xs: 1, sm: 1.5 },
                    fontSize: { xs: 12, sm: 14 },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                      userSelect: "none",
                    }}
                    role="button"
                    tabIndex={0}
                    title={String(column.header)}
                    onClick={() => handleOrderByChange(undefined as any, column.key)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleOrderByChange(undefined as any, column.key);
                      }
                    }}
                  >
                    {column.header}
                    {orderBy === column.key && (
                      <span style={{ marginLeft: 4 }}>
                        {sortDirection === "asc" ? "▲" : "▼"}
                      </span>
                    )}
                  </Box>
                  {column.filterable &&
                    getFilterInput<T>(column, columnFilters, handleFilterChange)}
                </TableCell>
              ))}
              {actionButtons?.length && (
                <TableCell sx={{ py: { xs: 0.75, sm: 1 }, px: { xs: 1, sm: 1.5 } }} />
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {flattenedData?.length ? (
              <>
                {flattenedData.map((item: any, idx: number) => {
                  const rowKey =
                    item.id ??
                    item[columns[0]?.key] ??
                    item.year ??
                    `${columns[0]?.key || "row"}-${idx}`;
                  return (
                    <TableRow
                      key={rowKey}
                      hover
                      onClick={() => handleRowClick(item)}
                      sx={{
                        cursor: disableRowClick ? "default" : "pointer",
                        backgroundColor: idx % 2 === 0 ? "#f9f9f9" : "#fff",
                        "&:hover": { backgroundColor: "#e0f7fa" },
                      }}
                    >
                      {columns.map((column) => (
                        <TableCell
                          key={column.key}
                          sx={{
                            userSelect: "text",
                            verticalAlign: "middle",
                            height: "50px",
                            py: { xs: 0.75, sm: 1 },
                            px: { xs: 1, sm: 1.5 },
                            fontSize: { xs: 12, sm: 14 },
                          }}
                          component="div"
                        >
                          <Box sx={{ width: "100%", pl: 1 }}>
                            {formatValue(
                              column.render
                                ? column.render(item)
                                : item[column.key] ||
                                  item[column.additionalKey as any],
                              column
                            ) || "-"}
                          </Box>
                        </TableCell>
                      ))}
                      {actionButtons?.length && (
                        <TableCell sx={{ py: { xs: 0.75, sm: 1 }, px: { xs: 1, sm: 1.5 } }}>
                          {actionButtons.map((button, aIdx) => (
                            <IconButton
                              key={`${rowKey}-action-${aIdx}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                button.onClick && button.onClick(e as React.MouseEvent<HTMLButtonElement>, item);
                              }}
                              color={button.color}
                              size="small"
                              sx={{ minWidth: 32, mx: 0.5 }}
                            >
                              {button.icon}
                            </IconButton>
                          ))}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
                {totalColumn && (
                  <TableRow key="total-row">
                    {columns.map((column, index) => (
                      <TableCell key={`total-${index}`}>
                        {totalColumn === column.key && (
                          <Chip color="success" label={`${t("general.total")}: ${totalAmount}`} />
                        )}
                      </TableCell>
                    ))}
                    {actionButtons?.length && <TableCell />}
                  </TableRow>
                )}
              </>
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length + (actionButtons?.length ? 1 : 0)}>
                  <Box sx={{ textAlign: "center", py: 2 }}>
                    <Typography variant="body2">{t("dataTable.noData")}</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell colSpan={columns.length + (actionButtons?.length ? 1 : 0)}>
                <Grid container alignItems="center">
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Select
                      size="small"
                      value={pageSize}
                      onChange={(e) => handlePageSizeChange(e as any, { value: e.target.value })}
                      sx={{ minWidth: 80 }}
                    >
                      {pageSizeOptions.map((size) => (
                        <MenuItem key={size} value={size}>{size}</MenuItem>
                      ))}
                    </Select>
                    <span style={{ marginLeft: "5px" }}>
                      {t("dataTable.rowsPerPage", { count: data.totalRecords })}
                    </span>
                  </Grid>
                  <Grid size={{ xs: 12, md: 8 }} sx={{ textAlign: "right" }}>
                    <Pagination
                      count={totalPages}
                      page={currentPage}
                      onChange={(_, page) => handlePageChange(page)}
                      color="primary"
                      sx={{ display: "inline-block", verticalAlign: "middle" }}
                    />
                    {exportAgentMethod != undefined && (
                      <Button
                        sx={{ mt: 1, ml: 2 }}
                        onClick={exportToExcel}
                        color="success"
                        variant="contained"
                        size="small"
                      >
                        {t("Excel")}
                      </Button>
                    )}
                  </Grid>
                </Grid>
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </Box>
  );
}

function getFilterInput<T extends object>(
  column: DataTableColumn<T>,
  columnFilters: { [key: string]: string },
  handleFilterChange: (
    columnKey: string,
    value: any,
    dataType?: DataType
  ) => void
): React.ReactNode {
  if (column.dataType === DataType.SELECT) {
    return (
      <Box sx={{ minWidth: 120 }}>
        <Select
          size="small"
          displayEmpty
          value={columnFilters[column.key] || column.defaultValue || ""}
          onChange={(e) =>
            handleFilterChange(column.key, e.target.value, column.dataType)
          }
          variant="standard"
          sx={{
            maxHeight: "26px !important",
            fontSize: 16,
            '& .MuiSelect-select': {
              paddingTop: '8px',
              paddingBottom: '8px',
              marginTop: '0px !important'
            },
          }}
        >
          <MenuItem value="">
            <em>{column.header}</em>
          </MenuItem>
          {(column.options || []).map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.text}
            </MenuItem>
          ))}
        </Select>
      </Box>
    );
  }

  if (column.dataType === DataType.DATE) {
    return (
      <Grid container spacing={1} sx={{ maxWidth: 280 }}>
        <Grid size={{ xs: 6 }}>
          <Input
            size="small"
            type="date"
            value={columnFilters[`${column.key}Start`] || ""}
            onChange={(e) =>
              handleFilterChange(
                `${column.key}Start`,
                e.target.value,
                column.dataType
              )
            }
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 6 }}>
          <Input
            size="small"
            type="date"
            value={columnFilters[`${column.key}End`] || ""}
            onChange={(e) =>
              handleFilterChange(
                `${column.key}End`,
                e.target.value,
                column.dataType
              )
            }
            fullWidth
          />
        </Grid>
      </Grid>
    );
  }

  if (column.dataType === DataType.BOOLEAN) {
    return (
      <Box sx={{ minWidth: 120 }}>
        <Select
          size="small"
          displayEmpty
          value={columnFilters[column.key] || ""}
          onChange={(e) =>
            handleFilterChange(column.key, e.target.value === "true", column.dataType)
          }
          variant="standard"
          sx={{
            maxHeight: "26px !important",
            fontSize: 16,
            '& .MuiSelect-select': {
              paddingTop: '8px',
              paddingBottom: '8px',
              marginTop: '0px !important'
            },
          }}
        >
          <MenuItem value="">
            <em>{column.header}</em>
          </MenuItem>
          <MenuItem value="true">{translate('general.yes')}</MenuItem>
          <MenuItem value="false">{translate('general.no')}</MenuItem>
        </Select>
      </Box>
    );
  }

  return (
    <Box sx={{ minWidth: 120 }}>
      <Input
        size="small"
        type={column.dataType || DataType.TEXT}
        placeholder={column.header}
        value={
          columnFilters[column.key] !== undefined
            ? columnFilters[column.key]
            : ""
        }
        onChange={(e) =>
          handleFilterChange(column.key, e.target.value, column.dataType)
        }
        fullWidth
      />
    </Box>
  );
}

export enum DataType {
  NUMBER = "number",
  TEXT = "text",
  DATE = "date",
  BOOLEAN = "boolean",
  SELECT = "select",
}

type DataTableColumn<T> = {
  key: string;
  text?: string;
  additionalKey?: string;
  header: string;
  render?: (item: T) => React.ReactNode;
  dataType?: DataType;
  filterable?: boolean;
  options?: any[];
  width?: string;
  defaultValue?: any;
  formatPrice?: boolean;
};

type DataTableActionButtons<T> = {
  icon?: React.ReactNode;
  color?: "primary" | "secondary" | "error" | "info" | "success" | "warning" | any;
  onClick?: (e: React.MouseEvent, item: T) => void;
};

interface Props<T> {
  data: DataTableList<T>;
  columns: DataTableColumn<T>[];
  defaultPageSize?: number;
  defaultOrderByColumn?: string;
  onFilter?: (filterProps: FilterProps) => void;
  onClick?: (item: T) => void;
  actionButtons?: DataTableActionButtons<T>[];
  exportAgentMethod?: (filters?: FilterProps) => any;
  clickUrl?: (item: T) => string;
  totalColumn?: string;
  disableRowClick?: boolean;
}

export type FilterProps = {
  filters?: any;
  page?: number;
  pageSize?: number;
  orderBy?: string;
  sortDirection?: string;
};
